import { BrowserRouter, HashRouter, Navigate, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import "@/App.css";
import { trackAppOpen } from "@/lib/analytics";
import { isNative } from "@/lib/native";

import AppShell from "@/components/AppShell";
import PrivacyConsent from "@/components/PrivacyConsent";
import Landing from "@/pages/Landing";
import OnboardingV5 from "@/pages/OnboardingV5";
import Auth from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import HomeV5 from "@/pages/HomeV5";
import Quiz from "@/pages/Quiz";
import Results from "@/pages/Results";
import Practices from "@/pages/Practices";
import StepForward from "@/pages/StepForward";
import SafeReach from "@/pages/SafeReach";
import Journal from "@/pages/Journal";
import Mosaic from "@/pages/Mosaic";
import Dashboard from "@/pages/Dashboard";
import Zen from "@/pages/Zen";
import Protect from "@/pages/Protect";
import Drafts from "@/pages/Drafts";
import Letters from "@/pages/Letters";
import Settings from "@/pages/Settings";
import Backup from "@/pages/Backup";
import Privacy from "@/pages/Privacy";
import Tools from "@/pages/Tools";
import You from "@/pages/You";
import Terms from "@/pages/Terms";
import About from "@/pages/About";
import Help from "@/pages/Help";
import Reset from "@/pages/Reset";
import Toolkit from "@/pages/Toolkit";
import Progress from "@/pages/Progress";
import Premium from "@/pages/Premium";
import LivingBook from "@/livingBook/LivingBook";
import Talk from "@/pages/Talk";
import Streaks from "@/pages/Streaks";
import SafeMode from "@/pages/SafeMode";
import FutureFunds from "@/pages/FutureFunds";

const Router = process.env.REACT_APP_OFFLINE_PREVIEW === "true" ? HashRouter : BrowserRouter;

function RootEntry() {
  const standalone = typeof window !== "undefined" && (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator?.standalone === true
  );
  return isNative() || standalone ? <Navigate to="/home" replace /> : <Landing />;
}

export default function App() {
  useEffect(() => { trackAppOpen(); }, []);
  return (
    <Router basename={process.env.REACT_APP_OFFLINE_PREVIEW === "true" ? undefined : process.env.PUBLIC_URL || "/"}>
      <AnimatePresence mode="wait">
        <Routes>
          {/* Public business + account routes */}
          <Route path="/" element={<RootEntry />} />
          <Route path="/start" element={<OnboardingV5 />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/help" element={<Help />} />

          {/* Private-use app routes inside the standard shell. Account is optional. */}
          <Route element={<AppShell />}>
            <Route path="/home" element={<HomeV5 />} />
            <Route path="/check-in" element={<Quiz />} />
            <Route path="/results/:id?" element={<Results />} />
            <Route path="/practices" element={<Practices />} />
            <Route path="/step-forward" element={<StepForward />} />
            <Route path="/safereach" element={<SafeReach />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/mosaic" element={<Mosaic />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/zen" element={<Zen />} />
            <Route path="/protect" element={<Protect />} />
            <Route path="/drafts" element={<Drafts />} />
            <Route path="/letters" element={<Letters />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/backup" element={<Backup />} />
            <Route path="/tools" element={<Tools />} />
            <Route path="/you" element={<You />} />
            <Route path="/account" element={<Account />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/toolkit" element={<Toolkit />} />
            <Route path="/progress" element={<Progress />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/living-book/*" element={<LivingBook />} />
            <Route path="/talk" element={<Talk />} />
            <Route path="/streaks" element={<Streaks />} />
            <Route path="/go-deeper" element={<Navigate to="/premium" replace />} />
            <Route path="/peace-compass" element={<HomeV5 />} />
            <Route path="/safe-mode" element={<SafeMode />} />
            <Route path="/future-funds" element={<FutureFunds />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Route>
        </Routes>
      </AnimatePresence>
      <PrivacyConsent />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: "#FFFAF5",
            color: "#2A1B2A",
            border: "1px solid rgba(91,57,84,0.10)",
            borderRadius: "1rem",
            fontFamily: "DM Sans, sans-serif",
          },
        }}
      />
    </Router>
  );
}
